/**
 * ч.1074: «Акаунт вимагає двофакторний вхід».
 *
 * - Двофакторний вхід уже налаштовано — підтвердити ЦЕЙ вхід кодом
 *   (застосунок або Telegram), без повторного входу з паролем.
 * - Ще не налаштовано — налаштувати просто тут: сторінка вибору акаунта
 *   працює без обраного акаунта, тож «Обліковий запис» звідси недоступний.
 *
 * `StepUpForm` — те саме підтвердження кодом для вкладки «Безпека» (власник
 * вмикає вимогу лише з підтвердженої сесії).
 */
import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { account, type StepUpStart } from '@/api/auth';
import { extractApiError } from '@/utils/extractApiError';

function CodeInput({ value, onChange, autoFocus }: {
  value: string; onChange: (v: string) => void; autoFocus?: boolean;
}) {
  return (
    <input value={value} inputMode="numeric" maxLength={6} autoFocus={autoFocus}
      autoComplete="one-time-code" aria-label="Код підтвердження"
      placeholder="123456" style={{ maxWidth: 140 }}
      onChange={(e) => onChange(e.target.value.replace(/\D/g, ''))} />
  );
}

/** Підтвердити поточний вхід кодом (сесія стане «з двофакторним входом»). */
export function StepUpForm({ onDone }: { onDone: () => void }) {
  const [start, setStart] = useState<StepUpStart | null>(null);
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const begin = useMutation({
    mutationFn: account.stepUpStart,
    onSuccess: (d) => { setStart(d); setCode(''); setError(null); },
    onError: (e) => setError(extractApiError(e, 'Не вдалося почати підтвердження')),
  });
  const confirm = useMutation({
    mutationFn: () => account.stepUp(start!.mfa_token, code),
    onSuccess: () => onDone(),
    onError: (e) => { setError(extractApiError(e, 'Невірний код')); setCode(''); },
  });
  // Одразу починаємо (для Telegram це і є «надіслати код»); лише раз.
  const started = useRef(false);
  useEffect(() => {
    if (!started.current) { started.current = true; begin.mutate(); }
  }, [begin]);

  return (
    <div>
      <p style={{ margin: '0 0 0.6rem', fontSize: '0.9rem' }}>
        {start?.two_factor_method === 'TELEGRAM'
          ? 'Ми надіслали код у Telegram. Введіть його нижче.'
          : 'Введіть 6-значний код із застосунку Google Authenticator.'}
      </p>
      <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
        <CodeInput value={code} onChange={setCode} autoFocus />
        <button type="button" className="primary"
          disabled={!start || code.length !== 6 || confirm.isPending}
          onClick={() => { setError(null); confirm.mutate(); }}>
          {confirm.isPending ? 'перевірка…' : 'підтвердити'}
        </button>
        <button type="button" disabled={begin.isPending} onClick={() => begin.mutate()}>
          {start?.two_factor_method === 'TELEGRAM' ? 'надіслати код ще раз' : 'почати знову'}
        </button>
      </div>
      {error && <div className="error">{error}</div>}
      <p style={{ margin: '0.6rem 0 0', fontSize: '0.82rem', color: 'var(--text-secondary)' }}>
        Немає доступу до коду (втрачено телефон)?{' '}
        <Link to="/account-recovery/2fa">Відновити вхід через підтримку</Link>
      </p>
    </div>
  );
}

/** Налаштування застосунку: QR → код → увімкнено (і вхід одразу підтверджено). */
function TotpSetup({ onDone }: { onDone: () => void }) {
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const setup = useMutation({
    mutationFn: () => account.totpSetup(),
    onError: (e) => setError(extractApiError(e)),
  });
  const enable = useMutation({
    mutationFn: () => account.totpEnable(code),
    onSuccess: () => onDone(),
    onError: (e) => { setError(extractApiError(e, 'Невірний код')); setCode(''); },
  });
  if (!setup.data) {
    return (
      <div>
        <button type="button" className="primary" disabled={setup.isPending}
          onClick={() => { setError(null); setup.mutate(); }}>
          налаштувати Google Authenticator
        </button>
        {error && <div className="error">{error}</div>}
      </div>
    );
  }
  return (
    <div>
      <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', alignItems: 'flex-start' }}>
        {setup.data.qr_data_uri && (
          <img src={setup.data.qr_data_uri} alt="QR-код для Google Authenticator"
            style={{ width: 160, height: 160, borderRadius: 8, background: 'var(--bg-panel)',
              border: '1px solid var(--border)' }} />
        )}
        <ol style={{ margin: 0, paddingLeft: '1.2rem', fontSize: '0.85rem', flex: 1, minWidth: 200 }}>
          <li>Встановіть Google Authenticator на телефон.</li>
          <li>«Додати» → «Сканувати QR-код» і наведіть камеру на код.</li>
          <li>Введіть 6-значний код із застосунку.</li>
          <li style={{ color: 'var(--text-secondary)' }}>
            Без камери — ключ вручну: <code style={{ fontWeight: 700 }}>{setup.data.secret}</code>
          </li>
        </ol>
      </div>
      <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', marginTop: '0.6rem' }}>
        <CodeInput value={code} onChange={setCode} autoFocus />
        <button type="button" className="primary" disabled={code.length !== 6 || enable.isPending}
          onClick={() => { setError(null); enable.mutate(); }}>
          {enable.isPending ? 'перевірка…' : 'увімкнути і продовжити'}
        </button>
      </div>
      {error && <div className="error">{error}</div>}
    </div>
  );
}

/** Підключення Telegram: бот → Start → увімкнути код у Telegram → підтвердити. */
function TelegramSetup({ ready, botConfigured, onReady }: {
  ready: boolean; botConfigured: boolean; onReady: () => void;
}) {
  const qc = useQueryClient();
  const [waiting, setWaiting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const link = useMutation({
    mutationFn: () => account.telegramLink(),
    onSuccess: (d) => { window.open(d.url, '_blank', 'noopener'); setWaiting(true); },
    onError: (e) => setError(extractApiError(e)),
  });
  const useTelegram = useMutation({
    mutationFn: () => account.setTwoFactorMethod('TELEGRAM'),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['account-security'] });
      onReady();
    },
    onError: (e) => setError(extractApiError(e)),
  });
  useEffect(() => {
    if (!waiting || ready) return;
    const id = setInterval(() => qc.invalidateQueries({ queryKey: ['account-security'] }), 3000);
    return () => clearInterval(id);
  }, [waiting, ready, qc]);

  if (!botConfigured) {
    return (
      <div style={{ color: 'var(--text-secondary)', fontSize: '0.85rem' }}>
        Код у Telegram зараз недоступний (бота не налаштовано на сервері) —
        скористайтесь Google Authenticator.
      </div>
    );
  }
  return (
    <div>
      {ready ? (
        <button type="button" className="primary" disabled={useTelegram.isPending}
          onClick={() => { setError(null); useTelegram.mutate(); }}>
          увімкнути вхід з кодом у Telegram
        </button>
      ) : (
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
          <button type="button" disabled={link.isPending}
            onClick={() => { setError(null); link.mutate(); }}>
            підключити Telegram
          </button>
          {waiting && (
            <span style={{ color: 'var(--text-secondary)', fontSize: '0.85rem' }}>
              Відкрили бота — натисніть у ньому «Start»…
            </span>
          )}
        </div>
      )}
      {error && <div className="error">{error}</div>}
    </div>
  );
}

/** Екран для акаунта, що вимагає двофакторний вхід. */
export default function TwoFactorGate({ onDone }: { onDone: () => void }) {
  const secQ = useQuery({ queryKey: ['account-security'], queryFn: account.security });
  const [confirmTelegram, setConfirmTelegram] = useState(false);
  const sec = secQ.data;
  if (secQ.isLoading || !sec) return <div>Завантаження…</div>;

  if (sec.two_factor_method !== 'NONE' || confirmTelegram) {
    return (
      <div>
        <p style={{ marginTop: 0, fontSize: '0.9rem', color: 'var(--text-secondary)' }}>
          Цей вхід ще не підтверджено другим кроком. Підтвердіть його кодом —
          входити знову не потрібно.
        </p>
        <StepUpForm onDone={onDone} />
      </div>
    );
  }
  return (
    <div>
      <p style={{ marginTop: 0, fontSize: '0.9rem', color: 'var(--text-secondary)' }}>
        Двофакторний вхід у вас ще не налаштовано. Окрім пароля система
        питатиме одноразовий код — оберіть, звідки його брати:
      </p>
      <div className="card" style={{ marginBottom: '0.75rem' }}>
        <b>Google Authenticator</b>
        <div style={{ marginTop: '0.5rem' }}><TotpSetup onDone={onDone} /></div>
      </div>
      <div className="card">
        <b>Код у Telegram</b>
        <div style={{ marginTop: '0.5rem' }}>
          <TelegramSetup ready={sec.telegram_ready} botConfigured={sec.telegram_bot_configured}
            onReady={() => setConfirmTelegram(true)} />
        </div>
      </div>
    </div>
  );
}
